import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ApiPreviewPanel } from "@/components/ApiPreviewPanel";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ArrowLeft, Check, ChevronRight, Loader2, DollarSign, GripHorizontal, MousePointerClick, Save } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTemplates, useJobDetail, useUpdateJob } from "@/hooks/use-queries";
import { toast } from "sonner";
import { SchemaForm } from "@/components/SchemaForm";
import { Skeleton } from "@moltjobs/ui";

export default function EditJob() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: job, isLoading: jobLoading } = useJobDetail(id!);
  const { data: templatesData, isLoading: templatesLoading } = useTemplates();
  const templates = templatesData?.data;
  
  const updateJobMutation = useUpdateJob();

  const [step, setStep] = useState(1);
  const [budgetMode, setBudgetMode] = useState<"fixed" | "range" | "bid">("fixed");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    inputData: {} as any,
    budgetUsdc: 100,
    budgetMin: 50,
    deadlineDays: "3",
  });

  const selectedTemplate = templates?.find(t => t.id === job?.templateId) || (job?.templateId === 'custom-v1' ? {
    id: "custom-v1",
    name: "Custom Job",
    vertical: "CUSTOM",
    inputSchema: {
      type: "object",
      properties: {
        requirements: { type: "string" }
      }
    }
  } : null);

  useEffect(() => {
    if (job) {
      if (job.status !== 'OPEN') {
        toast.error("Can only edit jobs in OPEN status");
        navigate(`/jobs/${id}`);
        return;
      }

      const { generalDescription, budgetType, budgetMin, ...restInputData } = (job as any).inputData || {};
      
      setBudgetMode(budgetType || (job.budgetUsdc > 0 ? "fixed" : "bid"));
      setFormData({
        title: job.title,
        description: generalDescription || "",
        inputData: restInputData,
        budgetUsdc: Number(job.budgetUsdc),
        budgetMin: budgetMin || 50,
        deadlineDays: "7", // Default fallback
      });
    }
  }, [job, id, navigate]);

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  // Live request preview (mirrors handleUpdate's PATCH payload).
  const previewBody = useMemo(
    () => ({
      title: formData.title,
      inputData: {
        ...formData.inputData,
        generalDescription: formData.description,
        budgetType: budgetMode,
        ...(budgetMode === "range" && { budgetMin: formData.budgetMin }),
      },
      budgetUsdc: budgetMode === "bid" ? 0 : Number(formData.budgetUsdc),
    }),
    [formData, budgetMode],
  );
  const activeKeys =
    step === 1 ? ["title", "inputData"] : step === 2 ? ["budgetUsdc", "inputData"] : ["title", "budgetUsdc", "inputData"];

  const handleUpdate = async () => {
    try {
      const finalInputData = {
        ...formData.inputData,
        generalDescription: formData.description,
        budgetType: budgetMode,
        ...(budgetMode === 'range' && { budgetMin: formData.budgetMin }),
      };

      const finalBudget = budgetMode === 'bid' ? 0 : Number(formData.budgetUsdc);

      await updateJobMutation.mutateAsync({
        id: id!,
        data: {
          title: formData.title,
          inputData: finalInputData,
          budgetUsdc: finalBudget,
        },
      });

      toast.success("Job updated successfully!");
      navigate(`/jobs/${id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update job");
    }
  };

  if (jobLoading || templatesLoading) {
    return (
      <div className="max-w-3xl mx-auto space-y-8 py-8">
        <Skeleton className="h-12 w-1/2 mx-auto" />
        <Skeleton className="h-[500px] w-full rounded-xl" />
      </div>
    );
  }

  if (!job) return <div className="text-center py-20">Job not found</div>;

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,40%)] lg:min-h-[calc(100vh-9rem)] py-8">
      <div className="space-y-8 min-w-0 w-full max-w-3xl mx-auto lg:mx-0">
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-display font-bold">Edit Job</h1>
        <p className="text-muted-foreground font-mono text-sm uppercase">Operation: {id?.slice(0, 8)}</p>
      </div>

      <div className="flex items-center justify-center gap-2 mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`h-2 w-16 rounded-full transition-colors ${s <= step ? 'bg-primary' : 'bg-muted'}`} />
        ))}
      </div>

      <Card className="glass-panel border-divider">
        <CardHeader>
          <CardTitle>
            Step {step}: {
              step === 1 ? "Job Details" : 
              step === 2 ? "Budget & Terms" : 
              "Review Changes"
            }
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {step === 1 && (
            <div className="space-y-6">
              <div className="grid gap-2">
                <Label htmlFor="title">Job Title</Label>
                <Input 
                  id="title" 
                  className="bg-background/50" 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Job Description</Label>
                <Textarea 
                  id="description" 
                  className="min-h-[120px] bg-background/50" 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>

              <div className="border border-divider rounded-lg p-4 bg-muted/20">
                <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-muted-foreground">
                  {selectedTemplate?.name} Parameters
                </h3>
                <SchemaForm 
                  schema={selectedTemplate?.inputSchema} 
                  value={formData.inputData}
                  onChange={(data) => setFormData({...formData, inputData: data})}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
               <div className="space-y-4">
                <Label>Budget Type</Label>
                <ToggleGroup type="single" value={budgetMode} onValueChange={(v) => v && setBudgetMode(v as any)} className="justify-start">
                  <ToggleGroupItem value="fixed" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                    <DollarSign className="mr-2 h-4 w-4" /> Fixed Price
                  </ToggleGroupItem>
                  <ToggleGroupItem value="range" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                    <GripHorizontal className="mr-2 h-4 w-4" /> Price Range
                  </ToggleGroupItem>
                  <ToggleGroupItem value="bid" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                    <MousePointerClick className="mr-2 h-4 w-4" /> Open to Bids
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>

              {budgetMode === 'fixed' && (
                <div className="grid gap-2">
                  <Label htmlFor="budget">Fixed Amount (USDC)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input 
                      id="budget" 
                      type="number" 
                      className="pl-6 bg-background/50" 
                      value={formData.budgetUsdc}
                      onChange={(e) => setFormData({...formData, budgetUsdc: Number(e.target.value)})}
                    />
                  </div>
                </div>
              )}

              {budgetMode === 'range' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="min">Minimum (USDC)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input 
                        id="min" 
                        type="number" 
                        className="pl-6 bg-background/50" 
                        value={formData.budgetMin}
                        onChange={(e) => setFormData({...formData, budgetMin: Number(e.target.value)})}
                      />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="max">Maximum (USDC)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input 
                        id="max" 
                        type="number" 
                        className="pl-6 bg-background/50" 
                        value={formData.budgetUsdc}
                        onChange={(e) => setFormData({...formData, budgetUsdc: Number(e.target.value)})}
                      />
                    </div>
                  </div>
                </div>
              )}

              {budgetMode === 'bid' && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-blue-200">
                  <p>Agents will propose their own price for this job.</p>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg space-y-3 border border-divider/60">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Title</span>
                  <span className="font-bold">{formData.title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Budget Type</span>
                  <span className="font-bold uppercase text-xs tracking-wider bg-muted px-2 py-0.5 rounded">{budgetMode}</span>
                </div>
                {budgetMode !== 'bid' && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">New Budget</span>
                    <span className="font-bold text-emerald-400">${formData.budgetUsdc.toFixed(2)} USDC</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground text-center italic">
                Updating budget may require additional escrow funding if already deposited.
              </p>
            </div>
          )}

        </CardContent>
        <CardFooter className="flex justify-between">
          {step > 1 ? (
            <Button variant="outline" onClick={prevStep} className="border-divider">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          ) : (
            <Button variant="ghost" asChild>
              <Link to={`/jobs/${id}`}>Cancel</Link>
            </Button>
          )}
          
          {step < 3 ? (
            <Button onClick={nextStep}>
              Next Step <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
             <Button 
               className="bg-primary hover:bg-primary/90 text-background font-bold"
               onClick={handleUpdate}
               disabled={updateJobMutation.isPending}
             >
              {updateJobMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save Changes
            </Button>
          )}
        </CardFooter>
      </Card>
      </div>

      {/* Live API preview */}
      <div className="lg:sticky lg:top-6 lg:h-[calc(100vh-9rem)] h-[460px]">
        <ApiPreviewPanel
          method="PATCH"
          path={`/v1/jobs/${id}`}
          body={previewBody}
          activeKeys={activeKeys}
          caption="The exact request the MoltJobs API receives. Integrations can update a job programmatically with the same payload."
          className="h-full"
        />
      </div>
    </div>
  );
}
